import { dateTimeUtils, IDateTimeBox, IFormField } from "cayo.ui";
import { IUIItem } from "@cayo/shared/api/types/schema";
import { changesPrefix } from "@cayo/shared/components/Form/modelUtils/constants";
import { getChangedPath } from "@cayo/shared/components/Form/modelUtils/getChangedPath";
import logger from "@cayo/shared/libs/logger";
import { objectUtils } from "@cayo/shared/utils/object";
import { getFlatItems } from "./build-model";

const log = logger.getLogger("model-utils");

export const deleteModelProperty = (property: string, target: any) => {
  property = getChangedPath(property);

  if (!target) {
    log.debug("deleteModelProperty: target is empty");
    return;
  }

  if (!property) {
    log.debug("deleteModelProperty: property is empty");
    return;
  }

  if (property.indexOf(".") === -1) {
    delete target[property];
  } else {
    const parts = property.split(".");
    const changes = target[parts[0]];
    if (changes) {
      delete changes[parts[1]];
    }
  }
};

function isPathPresent(object: any, path: string) {
  const keys = path.split(".");
  let current = object;

  for (const key of keys) {
    if (current && key in current) {
      current = current[key];
    } else {
      return false;
    }
  }

  return true;
}

export const isPropChanged = (model: any, prop: string) => {
  if (!model) {
    return undefined;
  }

  const changes = model[changesPrefix];

  const result = changes ? isPathPresent(changes, prop) : undefined;
  return result;
};

const createProps = (model: any, propPath: string, propValue: any) => {
  const paths = propPath.split(".");
  let newModel = model;
  for (let i = 0; i < paths.length - 1; i++) {
    const path = paths[i];

    if (!newModel[path]) {
      newModel[path] = {};
    }

    newModel = newModel[path];
  }

  newModel[paths[paths.length - 1]] = propValue;
};

type SuspendMode = "suspend" | "undosuspend" | undefined;

const convertToNameValueDictionary2 = (o: any) => {
  const propNames = Object.keys(o);
  const result = propNames.map((k) => ({ name: k, value: o[k] }));
  return result;
};

const convertToNameValueDictionary = (o: any, items: IUIItem[]) => {
  const propNames = Object.keys(o);
  items = getFlatItems(items);
  items = items.filter((i) => propNames.findIndex((p) => i.name === p) >= 0);

  let suspendMode: SuspendMode = undefined;

  const result = propNames.map((k) => {
    const value = o[k];

    let strValue = "";
    const isSimple = objectUtils.isPrimitive(value);
    if (value instanceof Date) {
      strValue = dateTimeUtils.getUtcDate(value).toISOString();
    } else {
      if (isSimple) {
        const item = items.find((i) => i.name === k || `${i.name}Alt` === k);
        if (item) {
          if (item.type === "date-time-box" && (item as IDateTimeBox).editMode === "onlyTime") {
            const dateTime = dateTimeUtils.parseTime(value);
            if (!dateTime.isFormatInvalid) {
              strValue = dateTimeUtils.getUtcDate(dateTime.value!).toISOString();
            }
          } else {
            if (item.name?.toLowerCase() === "suspendmode" && value === false) {
              const isUndo = (item as IFormField).label?.toLowerCase().includes("undo");
              suspendMode = isUndo ? "undosuspend" : "suspend";
            }
            strValue = value?.toString();
          }
        } else {
          strValue = JSON.stringify(value);
        }
      } else {
        strValue = JSON.stringify(value);
      }
    }
    const r = { name: k, value: strValue };
    return r;
  });

  if (suspendMode !== undefined) {
    const defaultDate = dateTimeUtils.getUtcDate(new Date()).toISOString();
    if (suspendMode === "suspend") {
      result.push(
        ...[
          { name: "SuspendDate", value: defaultDate },
          { name: "SuspendTime", value: defaultDate },
        ]
      );
    } else {
      result.push(
        ...[
          { name: "UndoSuspendDate", value: defaultDate },
          { name: "UndoSuspendTime", value: defaultDate },
        ]
      );
    }
  }

  return result;
};

const getCayoClientTimeOffset = () => {
  return { CayoClientTimeOffset: new Date().getTimezoneOffset() / 60 };
};

const getBeforeValueParameters = (current: any, original: any) => {
  if (!original) {
    return undefined;
  }

  const changed = ["CayoClientTimeOffset"];
  const before: any = getCayoClientTimeOffset();
  Object.keys(current).forEach((k) => {
    changed.push(k);
    before[k] = original[k];
  });

  const value = JSON.stringify({ changed, before });
  return { name: "CayoBeforeValuesParam", value };
};

const getParameterValue = (parameters: any[] | undefined, name: string) => {
  const parameter = parameters?.find((item) => item?.name === name);

  if (typeof parameter?.value === "string") {
    return parameter.value;
  }

  if (parameter?.value === null || parameter?.value === undefined) {
    return "";
  }

  return String(parameter.value);
};

const setParameterValue = (parameters: any[] | undefined, name: string, value: string) => {
  const nextParameters = parameters ? [...parameters] : [];
  const existingParameter = nextParameters.find((item) => item?.name === name);

  if (existingParameter) {
    existingParameter.value = value;
    return nextParameters;
  }

  nextParameters.push({ name, value });
  return nextParameters;
};

// Removes properties that are blank (null, undefined, empty string)
// and were not present in the original model
// If a property has addedMembers and removedMembers subproperties, keep only those
const removeBlankProps = (object: any, originalModel?: any): any => {
  //const result: any = {};
  for (const key in object) {
    const currentValue = object[key];
    const originalValue = originalModel?.[key];

    if (
      originalValue === undefined &&
      (currentValue === "" || currentValue === null || currentValue === undefined)
    ) {
      delete object[key];
    }

    // If property has addedMembers and removedMembers, keep only those subproperties
    if (
      currentValue &&
      typeof currentValue === "object" &&
      !Array.isArray(currentValue) &&
      "addedMembers" in currentValue &&
      "removedMembers" in currentValue
    ) {
      object[key] = {
        addedMembers: currentValue.addedMembers,
        removedMembers: currentValue.removedMembers,
        // Include tgmMembers if present, as it may be needed for restrictedGroups
        // in certain operations (e.g., JoinToGroupsGrid)
        ...("tgmMembers" in currentValue ? { tgmMembers: currentValue.tgmMembers } : {}),
      };
    }
  }

  return object;
};

export const modelUtils = {
  createProps,
  convertToNameValueDictionary,
  convertToNameValueDictionary2,
  getBeforeValueParameters,
  getParameterValue,
  getCayoClientTimeOffset,
  removeBlankProps,
  setParameterValue,
};
